import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Settings } from '../../client/services';
import RedTimePicker from './RedTimePicker';

export interface OrderGapRule {
  id: string;
  from: string; // e.g. "10:00 PM" or "22:00"
  to: string; // e.g. "08:59 AM" or "08:59"
  deliveryStartDay?: 'same_day' | 'next_day';
  slots: string[]; // e.g. ["2 PM to 6 PM", "6 PM to 10 PM"]
}

export interface OrderGapConfig {
  allowAllSlots: boolean;
  rules: OrderGapRule[];
}

export interface OrderGapSettingsData {
  gap0: OrderGapConfig;
  gap1: OrderGapConfig;
}

const SETTING_NAME = 'order_gap_settings';

const DEFAULT_SETTINGS: OrderGapSettingsData = {
  gap0: { allowAllSlots: true, rules: [] },
  gap1: { allowAllSlots: true, rules: [] },
};

interface Props {
  availableSlots?: string[];
  onSaved?: () => void;
}

function timeToMinutes(t: string): number {
  if (!t) return 0;
  // Handle 12-hour AM/PM: "10:00 PM"
  const m12 = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const m = parseInt(m12[2], 10);
    const p = m12[3].toLowerCase();
    if (p === 'pm' && h !== 12) h += 12;
    if (p === 'am' && h === 12) h = 0;
    return h * 60 + m;
  }
  // Handle 24-hour: "22:00"
  const parts = t.split(':').map((v) => parseInt(v, 10));
  return (parts[0] || 0) * 60 + (parts[1] || 0);
}

function formatDisplayTime(t: string): string {
  if (!t) return '';
  // If already formatted with AM/PM
  if (/am|pm/i.test(t)) return t;
  const parts = t.split(':').map((v) => parseInt(v, 10));
  let h = parts[0] || 0;
  const m = String(parts[1] || 0).padStart(2, '0');
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
}

function getSegments(from: string, to: string): [number, number][] {
  const f = timeToMinutes(from);
  const t = timeToMinutes(to);
  return f < t ? [[f, t]] : [[f, 1440], [0, t]];
}

function rangesOverlap(
  aFrom: string,
  aTo: string,
  bFrom: string,
  bTo: string
): boolean {
  const segsA = getSegments(aFrom, aTo);
  const segsB = getSegments(bFrom, bTo);
  return segsA.some(([aStart, aEnd]) =>
    segsB.some(([bStart, bEnd]) => aStart < bEnd && aEnd > bStart)
  );
}

function getSlotStartMinutes(slot: string): number {
  const match = String(slot || '').match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!match) return 9999;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2] || '0', 10);
  const p = match[3].toLowerCase();
  if (p === 'pm' && h !== 12) h += 12;
  if (p === 'am' && h === 12) h = 0;
  return h * 60 + m;
}

function sortSlotsAscending(slots: string[], referenceSlots: string[] = []): string[] {
  return [...slots].sort((a, b) => {
    const idxA = referenceSlots.indexOf(a);
    const idxB = referenceSlots.indexOf(b);
    if (idxA !== -1 && idxB !== -1) {
      return idxA - idxB;
    }
    return getSlotStartMinutes(a) - getSlotStartMinutes(b);
  });
}

const OrderGapSettings: React.FC<Props> = ({
  availableSlots = ['11 AM to 2 PM', '2 PM to 6 PM', '6 PM to 10 PM'],
  onSaved,
}) => {
  const [activeGap, setActiveGap] = useState<0 | 1>(0);
  const [settings, setSettings] = useState<OrderGapSettingsData>(DEFAULT_SETTINGS);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Form state
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [fromTime, setFromTime] = useState<string>('');
  const [toTime, setToTime] = useState<string>('');
  const [deliveryStartDay, setDeliveryStartDay] = useState<'same_day' | 'next_day'>('same_day');
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [formError, setFormError] = useState<string>('');

  const effectiveSlots = availableSlots.length
    ? availableSlots
    : ['11 AM to 2 PM', '2 PM to 6 PM', '6 PM to 10 PM'];

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await Settings.find({
        query: {
          search: SETTING_NAME,
          limit: 10,
          skip: 0,
        },
      });
      const setting =
        (response?.data || []).find((item: any) => item.name === SETTING_NAME) ||
        null;

      if (setting?.value) {
        setSettingId(setting.id);
        const parsed =
          typeof setting.value === 'string'
            ? JSON.parse(setting.value)
            : setting.value;
        const sortRules = (cfg?: OrderGapConfig) => ({
          allowAllSlots: cfg ? cfg.allowAllSlots : true,
          rules: (cfg?.rules || []).map((r) => ({
            ...r,
            slots: sortSlotsAscending(r.slots || [], effectiveSlots),
          })),
        });
        setSettings({
          gap0: sortRules(parsed.gap0),
          gap1: sortRules(parsed.gap1),
        });
      } else {
        setSettingId(null);
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (err: any) {
      console.error('Failed to load order gap settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const saveToBackend = async (newSettings: OrderGapSettingsData) => {
    try {
      const payload = {
        name: SETTING_NAME,
        value: JSON.stringify(newSettings),
        valueType: 'json',
        language: '',
        activeState: true,
      };

      if (settingId) {
        await Settings.patch(settingId, payload);
      } else {
        const created = await Settings.create(payload);
        if (created?.id) setSettingId(created.id);
      }
      setSettings(newSettings);
      toast.success('Delivery slots settings saved');
      if (onSaved) onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save delivery slots settings');
    }
  };

  const currentConfig: OrderGapConfig =
    activeGap === 0 ? settings.gap0 : settings.gap1;

  const handleToggleAllowAll = async (allow: boolean) => {
    const updated: OrderGapSettingsData = {
      ...settings,
      [activeGap === 0 ? 'gap0' : 'gap1']: {
        ...currentConfig,
        allowAllSlots: allow,
      },
    };
    setIsFormOpen(false);
    setEditingRuleId(null);
    await saveToBackend(updated);
  };

  const handleOpenAddForm = () => {
    setEditingRuleId(null);
    setFromTime('');
    setToTime('');
    setDeliveryStartDay('same_day');
    setSelectedSlots([]);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditRule = (rule: OrderGapRule) => {
    setEditingRuleId(rule.id);
    setFromTime(rule.from);
    setToTime(rule.to);
    setDeliveryStartDay(rule.deliveryStartDay || 'same_day');
    setSelectedSlots(sortSlotsAscending(rule.slots || [], effectiveSlots));
    setFormError('');
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingRuleId(null);
    setFormError('');
  };

  const handleToggleSlot = (slot: string) => {
    setSelectedSlots((prev) => {
      const next = prev.includes(slot)
        ? prev.filter((s) => s !== slot)
        : [...prev, slot];
      return sortSlotsAscending(next, effectiveSlots);
    });
  };

  const handleSaveRule = async () => {
    if (!fromTime || !toTime) {
      setFormError('Enter both a from and to order time.');
      return;
    }
    if (fromTime === toTime) {
      setFormError('The from and to times cannot be the same.');
      return;
    }
    if (!selectedSlots.length) {
      setFormError('Select at least one slot.');
      return;
    }

    // Check overlap with other rules in the current gap
    const overlap = currentConfig.rules.some((r) => {
      if (editingRuleId && r.id === editingRuleId) return false;
      return rangesOverlap(fromTime, toTime, r.from, r.to);
    });

    if (overlap) {
      setFormError('This order time overlaps another setting. Adjust the range.');
      return;
    }

    const sortedSlots = sortSlotsAscending(selectedSlots, effectiveSlots);
    let updatedRules: OrderGapRule[];
    if (editingRuleId) {
      updatedRules = currentConfig.rules.map((r) =>
        r.id === editingRuleId
          ? {
              ...r,
              from: fromTime,
              to: toTime,
              deliveryStartDay,
              slots: sortedSlots,
            }
          : r
      );
    } else {
      const newRule: OrderGapRule = {
        id: `rule_${Date.now()}`,
        from: fromTime,
        to: toTime,
        deliveryStartDay,
        slots: sortedSlots,
      };
      updatedRules = [...currentConfig.rules, newRule];
    }

    const updated: OrderGapSettingsData = {
      ...settings,
      [activeGap === 0 ? 'gap0' : 'gap1']: {
        ...currentConfig,
        rules: updatedRules,
      },
    };

    handleCloseForm();
    await saveToBackend(updated);
  };

  const handleRemoveRule = async (ruleId: string) => {
    const updatedRules = currentConfig.rules.filter((r) => r.id !== ruleId);
    const updated: OrderGapSettingsData = {
      ...settings,
      [activeGap === 0 ? 'gap0' : 'gap1']: {
        ...currentConfig,
        rules: updatedRules,
      },
    };
    if (editingRuleId === ruleId) {
      handleCloseForm();
    }
    await saveToBackend(updated);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} sx={{ color: '#EE3750' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: '#FDF0F1',
        border: '1px solid #F8D3D8',
        borderRadius: '12px',
        p: { xs: 1.5, sm: 2 },
        mt: 2,
      }}
    >
      {/* Sub tabs: Delivery Gap 0 / Delivery Gap 1 */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          borderBottom: '1px solid #F8D3D8',
          pb: 0.5,
          mb: 2,
        }}
      >
        <Button
          onClick={() => {
            setActiveGap(0);
            setIsFormOpen(false);
            setEditingRuleId(null);
          }}
          sx={{
            color: activeGap === 0 ? '#EE3750' : '#6f6a70',
            fontWeight: activeGap === 0 ? 800 : 600,
            fontSize: '14px',
            borderBottom: activeGap === 0 ? '3px solid #EE3750' : '3px solid transparent',
            borderRadius: 0,
            textTransform: 'none',
            px: 1.5,
            pb: 1,
            '&:hover': { background: 'transparent' },
          }}
        >
          Delivery Gap 0
        </Button>
        <Button
          onClick={() => {
            setActiveGap(1);
            setIsFormOpen(false);
            setEditingRuleId(null);
          }}
          sx={{
            color: activeGap === 1 ? '#EE3750' : '#6f6a70',
            fontWeight: activeGap === 1 ? 800 : 600,
            fontSize: '14px',
            borderBottom: activeGap === 1 ? '3px solid #EE3750' : '3px solid transparent',
            borderRadius: 0,
            textTransform: 'none',
            px: 1.5,
            pb: 1,
            '&:hover': { background: 'transparent' },
          }}
        >
          Delivery Gap 1
        </Button>
      </Stack>

      {/* Question Banner */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
          border: '1px solid #F8D3D8',
          borderRadius: '10px',
          p: '12px 18px',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1b1b1f' }}>
          Do you want to allow all the slots?
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            gap: '4px',
            background: '#fff',
            border: '1px solid #ececec',
            borderRadius: '10px',
            p: '3px',
          }}
        >
          <Button
            size="small"
            onClick={() => handleToggleAllowAll(true)}
            sx={{
              background: currentConfig.allowAllSlots ? '#22c55e' : 'transparent',
              color: currentConfig.allowAllSlots ? '#fff' : '#6f6a70',
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: '8px',
              px: 2,
              minWidth: 48,
              textTransform: 'none',
              '&:hover': {
                background: currentConfig.allowAllSlots
                  ? '#1ea34d'
                  : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            Yes
          </Button>
          <Button
            size="small"
            onClick={() => handleToggleAllowAll(false)}
            sx={{
              background: !currentConfig.allowAllSlots ? '#FF4D4F' : 'transparent',
              color: !currentConfig.allowAllSlots ? '#fff' : '#6f6a70',
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: '8px',
              px: 2,
              minWidth: 48,
              textTransform: 'none',
              '&:hover': {
                background: !currentConfig.allowAllSlots
                  ? '#e03b3d'
                  : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            No
          </Button>
        </Box>
      </Box>

      {/* Custom Rules Section (Shown only when No is selected) */}
      {!currentConfig.allowAllSlots && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '13px', color: '#6f6a70' }}>
              Customers who order within an order time range see only the slots selected for it.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleOpenAddForm}
              sx={{
                background: '#EE3750',
                color: '#fff',
                fontWeight: 600,
                fontSize: '13px',
                borderRadius: '8px',
                textTransform: 'none',
                px: 2,
                '&:hover': { background: '#d42d44' },
              }}
            >
              Add Settings
            </Button>
          </Box>

          {/* Form */}
          {isFormOpen && (
            <Box
              component="form"
              onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                handleSaveRule();
              }}
              sx={{
                background: '#fff',
                border: '1px solid #F8D3D8',
                borderRadius: '12px',
                p: { xs: 2, sm: 2.5 },
                mb: 2,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 600, color: '#1b1b1f', mb: 0.5 }}
                  >
                    Order time: from
                  </Typography>
                  <RedTimePicker
                    value={fromTime}
                    onChange={(val) => setFromTime(val)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 600, color: '#1b1b1f', mb: 0.5 }}
                  >
                    Order time: to
                  </Typography>
                  <RedTimePicker
                    value={toTime}
                    onChange={(val) => setToTime(val)}
                  />
                </Box>
              </Stack>

              <Typography sx={{ fontSize: '12px', color: '#6f6a70', mb: 1.5 }}>
                If To is earlier than From, the range ends the next day (10:00 PM to 8:59 AM runs overnight).
              </Typography>

              {/* Delivery Starts From: Same Day vs Next Day Only */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 600, color: '#1b1b1f', mb: 0.75 }}
                >
                  Delivery starts from:
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    type="button"
                    variant={deliveryStartDay === 'same_day' ? 'contained' : 'outlined'}
                    onClick={() => setDeliveryStartDay('same_day')}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      borderRadius: '8px',
                      background: deliveryStartDay === 'same_day' ? '#EE3750' : '#fff',
                      borderColor: deliveryStartDay === 'same_day' ? '#EE3750' : '#F8D3D8',
                      color: deliveryStartDay === 'same_day' ? '#fff' : '#1b1b1f',
                      '&:hover': {
                        background: deliveryStartDay === 'same_day' ? '#d42d44' : '#FDF0F1',
                        borderColor: '#EE3750',
                      },
                    }}
                  >
                    Same day (Today)
                  </Button>
                  <Button
                    size="small"
                    type="button"
                    variant={deliveryStartDay === 'next_day' ? 'contained' : 'outlined'}
                    onClick={() => setDeliveryStartDay('next_day')}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      borderRadius: '8px',
                      background: deliveryStartDay === 'next_day' ? '#EE3750' : '#fff',
                      borderColor: deliveryStartDay === 'next_day' ? '#EE3750' : '#F8D3D8',
                      color: deliveryStartDay === 'next_day' ? '#fff' : '#1b1b1f',
                      '&:hover': {
                        background: deliveryStartDay === 'next_day' ? '#d42d44' : '#FDF0F1',
                        borderColor: '#EE3750',
                      },
                    }}
                  >
                    Next day only (No slots today)
                  </Button>
                </Stack>
                {deliveryStartDay === 'next_day' && (
                  <Typography sx={{ fontSize: '11px', color: '#EE3750', mt: 0.5, fontWeight: 500 }}>
                    Same-day delivery will be closed. Selected slots will be available starting from next day.
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 600, color: '#1b1b1f' }}
                >
                  {deliveryStartDay === 'next_day'
                    ? 'Slots selection for next day:'
                    : 'Slots selection:'}
                </Typography>
                <Button
                  size="small"
                  type="button"
                  onClick={() => setSelectedSlots(sortSlotsAscending([...effectiveSlots], effectiveSlots))}
                  sx={{
                    textTransform: 'none',
                    fontSize: '11px',
                    color: '#EE3750',
                    fontWeight: 600,
                    p: 0,
                    minWidth: 0,
                    '&:hover': { background: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Select all slots
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {effectiveSlots.map((slot) => {
                  const isSelected = selectedSlots.includes(slot);
                  return (
                    <Box
                      key={slot}
                      onClick={() => handleToggleSlot(slot)}
                      sx={{
                        cursor: 'pointer',
                        background: isSelected ? '#EE3750' : 'transparent',
                        borderColor: isSelected ? '#EE3750' : '#F8D3D8',
                        color: isSelected ? '#fff' : '#1b1b1f',
                        border: '1px solid',
                        borderRadius: '999px',
                        px: 1.75,
                        py: 0.75,
                        fontSize: '12px',
                        fontWeight: 600,
                        userSelect: 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: '#EE3750',
                        },
                      }}
                    >
                      {slot}
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={{ fontSize: '11.5px', color: selectedSlots.length ? '#1b1b1f' : '#6f6a70', mb: 1.5, background: '#FDF0F1', p: '6px 10px', borderRadius: '6px', border: '1px solid #F8D3D8' }}>
                {selectedSlots.length === 0
                  ? 'No slots selected. Click the chips above to choose which slots customers can book.'
                  : selectedSlots.length === effectiveSlots.length
                  ? `Customer will see: All slots (${selectedSlots.join(', ')})`
                  : `Customer will see: Only ${selectedSlots.join(', ')}`}
              </Typography>

              {formError && (
                <Typography sx={{ color: '#c0262d', fontSize: '12px', mb: 1.5 }}>
                  {formError}
                </Typography>
              )}

              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  sx={{
                    background: '#EE3750',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '13px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    px: 2,
                    '&:hover': { background: '#d42d44' },
                  }}
                >
                  {editingRuleId ? 'Update Settings' : 'Save Settings'}
                </Button>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  onClick={handleCloseForm}
                  sx={{
                    borderColor: '#EE3750',
                    color: '#EE3750',
                    fontWeight: 600,
                    fontSize: '13px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    px: 2,
                    '&:hover': { borderColor: '#d42d44', color: '#d42d44' },
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}

          {/* List of Rules */}
          {currentConfig.rules.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed #F8D3D8',
                background: '#FDF0F1',
                borderRadius: '12px',
                p: 3,
                textAlign: 'center',
                color: '#6f6a70',
                fontSize: '14px',
              }}
            >
              No order time settings yet. Select Add Settings to create one.
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {currentConfig.rules.map((rule) => {
                const overnight = timeToMinutes(rule.from) > timeToMinutes(rule.to);
                const isNextDayOnly = rule.deliveryStartDay === 'next_day';
                return (
                  <Box
                    key={rule.id}
                    sx={{
                      background: '#fff',
                      border: '1px solid #F8D3D8',
                      borderRadius: '10px',
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '13px',
                            color: '#1b1b1f',
                          }}
                        >
                          Order time {formatDisplayTime(rule.from)} – {formatDisplayTime(rule.to)}
                          {overnight ? ' (next day)' : ''}
                        </Typography>
                        <Chip
                          size="small"
                          label={isNextDayOnly ? 'Next day only' : 'Same day'}
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            fontWeight: 700,
                            background: isNextDayOnly ? '#FDF0F1' : '#eaf8ed',
                            color: isNextDayOnly ? '#EE3750' : '#1e7a2e',
                          }}
                        />
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {sortSlotsAscending(rule.slots || [], effectiveSlots).map((s) => (
                          <Chip
                            key={s}
                            label={s}
                            size="small"
                            sx={{
                              background: '#FDF0F1',
                              color: '#EE3750',
                              fontWeight: 700,
                              fontSize: '11px',
                              borderRadius: '999px',
                              border: 'none',
                              mb: 0.5,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditRule(rule)}
                        sx={{
                          borderColor: '#EE3750',
                          color: '#EE3750',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 1.5,
                          '&:hover': {
                            borderColor: '#d42d44',
                            color: '#d42d44',
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRemoveRule(rule.id)}
                        sx={{
                          borderColor: '#c0262d',
                          color: '#c0262d',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 1.5,
                          '&:hover': {
                            borderColor: '#a01c22',
                            color: '#a01c22',
                          },
                        }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default OrderGapSettings;
